// ---------------------------------------------------------------------------
// ChartViewLoader -- Progressive Loading with Steps (Felton DNA)
// ---------------------------------------------------------------------------
// Shows loading progress with individual steps for data processing.
// Uses capsule progress bars for visual consistency with chart DNA.
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type StepStatus = 'pending' | 'loading' | 'done'

export interface LoadingStep {
  readonly id: string
  readonly label: string
  status: StepStatus
  count?: number
  duration?: number
}

export interface ChartViewLoaderProps {
  readonly steps: readonly LoadingStep[]
  readonly currentStepIndex: number
  readonly totalCount?: number
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChartViewLoader({
  steps,
  currentStepIndex,
  totalCount,
}: ChartViewLoaderProps) {
  const [progress, setProgress] = useState(0)

  // Calculate overall progress
  useEffect(() => {
    if (steps.length === 0) return

    const doneCount = steps.filter((s) => s.status === 'done').length
    const loadingWeight = currentStepIndex < steps.length ? 0.5 : 0
    const newProgress = ((doneCount + loadingWeight) / steps.length) * 100

    setProgress(Math.min(100, newProgress))
  }, [steps, currentStepIndex])

  const currentStep = steps[currentStepIndex]

  return (
    <div className="rounded-xl border border-v11-border bg-v11-surface p-6 w-[500px] mx-auto">
      {/* Compact Header - Single Line */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm font-semibold text-v11-text-primary">
            Processing data
          </span>
          {totalCount !== undefined && (
            <span className="text-xs text-v11-text-muted">
              ({totalCount.toLocaleString()} records)
            </span>
          )}
        </div>
        <span className="text-xs font-bold text-primary tabular-nums">
          {Math.round(progress)}%
        </span>
      </div>

      {/* Compact Progress Bar */}
      <div className="h-1.5 rounded-full bg-v11-bg-secondary overflow-hidden mb-4">
        <motion.div
          className="h-full rounded-full bg-gradient-to-r from-primary to-primary/80"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
        />
      </div>

      {/* Single Step with Animation */}
      <AnimatePresence mode="wait">
        {currentStep && (
          <motion.div
            key={currentStep.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15, ease: 'easeInOut' }}
            className="flex items-center gap-2 px-2 py-1.5 rounded"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary flex-shrink-0" />
            <span className="text-xs text-v11-text-primary font-medium">
              {currentStep.label}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
